import { Button, Drawer, Grid2 } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { caseService } from "../../../_services/case.service";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { accountService } from "../../../_services/accounts.service";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import { DrawerHeader } from "../../../_components/drawer/header.drawer";
import { ClientContactManage } from "../../client/_components/client-contactManage";

export function CaseInfoManage(params: { onSave(): void }) {

    const resetData = () => ({});

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isClientContactDrawerOpen, setIsClientContactDrawerOpen] = useState(false);
    const [clientPocRefreshKey, setClientPocRefreshKey] = useState(0);
    const isAdmin = accountService.isAdmin || accountService.isUser;
    const navigate = useNavigate();

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return caseService
            .get(id)
            .then(
                (json) => {
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        return caseService
            .submit(data)
            .then(
                (json) => {
                    toastr.success("Saved successfully");
                    //Keep Open after Create so they can add subjects
                    if (!id && json) navigate("/Cases/Manage/" + json);
                    params.onSave();
                    fetchData();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return caseService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function openClientContactDrawer() {
        if (!data.clientId) {
            toastr.error("Please select a client first");
            return;
        }
        setIsClientContactDrawerOpen(true);
    }

    function closeClientContactDrawer() {
        setIsClientContactDrawerOpen(false);
    }

    function handleClientContactSaved() {
        setClientPocRefreshKey((current: number) => current + 1);
    }

    return (
        <ValidatorForm
            onSubmit={submit}
            noValidate>

            <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                <Grid2 size={{ xs: 12 }}>
                    <DataTypeAutoComplete
                        key="clientId"
                        dataType={DataTypeEnum.Clients}
                        label="Select Client"
                        hasParent={false}
                        value={data.clientId}
                        name={"clientId"}
                        onChange={handleChange}
                        required={true}
                        disabled={!isAdmin}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <Grid2 container spacing={1}>
                        <Grid2 size={{ xs: 9 }}>
                        <DataTypeAutoComplete
                            key={`clientContactId-${data.clientId || 0}-${clientPocRefreshKey}`}
                            dataType={DataTypeEnum.ClientContacts}
                            label="Select Client POC"
                            hasParent={true}
                            parentId={data.clientId}
                            value={data.clientContactId}
                            name={"clientContactId"}
                            onChange={handleChange}
                            required={true}
                        />
                        </Grid2>
                        <Grid2 size={{ xs: 3 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                disableElevation
                                sx={{ height: "56px", fontSize: "0.9rem !important", lineHeight: "1 !important" }}
                                onClick={openClientContactDrawer}
                                disabled={!data.clientId}
                            >
                                Add POC
                            </Button>
                        </Grid2>
                    </Grid2>
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="clientReference"
                        name="clientReference"
                        label="Client Reference"
                        fullWidth
                        value={data.clientReference}
                        onChange={handleChange}
                    />
                </Grid2>

                {isAdmin && <>
                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: data.id == null ? 12 : data?.isActive === false ? 12 : 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit">
                                    {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </StandardButton>
                            </Grid2>

                            {(!!id && data?.isActive === true) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="block"
                                        variant="error"
                                        onClick={disable}>
                                        Disable
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                </>}
            </Grid2>

            <Drawer
                anchor="right"
                open={isClientContactDrawerOpen}
                onClose={closeClientContactDrawer}
                ModalProps={{ keepMounted: true }}
            >
                <DrawerHeader name="Add Client Contact" onClose={closeClientContactDrawer} />
                {isClientContactDrawerOpen && (
                    <ClientContactManage clientId={data.clientId} onSaved={handleClientContactSaved} />
                )}
            </Drawer>

        </ValidatorForm>
    );
};